import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, AlertTriangle, RefreshCw, ClipboardCheck, Check, Archive, X } from "lucide-react";
import { API } from "@/contexts/AuthContext";

/**
 * FOH dashboard tile — "MOT Results Requiring Action".
 *
 * Lists the MOT-only sessions where the background DVSA poller has either
 * (a) auto-imported a result that hasn't been priced yet (PRICING_READY),
 * or (b) reached its hard-stop without seeing one (MOT_RESULT_PENDING_
 * MANUAL_REVIEW). Click a row to jump to the MOT workspace.
 */
const STATUS_PILL = {
  PRICING_READY: {
    cls: "bg-emerald-100 text-emerald-800 border-emerald-200",
    label: "Imported · price now",
  },
  MOT_RESULT_PENDING_MANUAL_REVIEW: {
    cls: "bg-amber-100 text-amber-800 border-amber-200",
    label: "Manual review needed",
  },
  CUSTOMER_CONTACT_REQUIRED: {
    cls: "bg-blue-100 text-blue-800 border-blue-200",
    label: "Price & send to customer",
  },
};

const MOTResultsRequiringActionTile = ({ refreshKey = 0 }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState({});
  const [confirmError, setConfirmError] = useState({});
  // Archive (dismiss-from-tile) state. We use an inline reason input
  // rather than a global confirm modal so the action stays fast +
  // keyboard-friendly. `archivePrompt` holds the row currently in
  // "are you sure?" mode; null means no row is in that state.
  const [archivePrompt, setArchivePrompt] = useState(null);
  const [archiveReason, setArchiveReason] = useState("");
  const [archiving, setArchiving] = useState({});
  const [archiveError, setArchiveError] = useState({});
  const navigate = useNavigate();

  const fetchRows = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/foh/mot-results-requiring-action`);
      setRows(res.data?.rows || []);
    } catch (_e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleConfirmPass = async (row) => {
    if (!row.job_id) return;
    setConfirming((s) => ({ ...s, [row.session_id]: true }));
    setConfirmError((s) => ({ ...s, [row.session_id]: null }));
    try {
      await axios.post(`${API}/foh/mot/${row.job_id}/confirm-pass`);
      // Drop the row locally so the UI feels instant.
      setRows((rs) => rs.filter((x) => x.session_id !== row.session_id));
    } catch (err) {
      setConfirmError((s) => ({
        ...s,
        [row.session_id]: err?.response?.data?.detail || "Could not confirm pass",
      }));
    } finally {
      setConfirming((s) => ({ ...s, [row.session_id]: false }));
    }
  };

  // Archive (dismiss this row from the tile). Hides the session from
  // the dashboard tile without marking the underlying job complete.
  // Reason captured for audit_trail so we can answer "why did this
  // disappear?" when a customer rings up. Two-step: click Archive →
  // optional reason → confirm.
  const startArchive = (row) => {
    setArchivePrompt(row.session_id);
    setArchiveReason("");
    setArchiveError((s) => ({ ...s, [row.session_id]: null }));
  };

  const cancelArchive = () => {
    setArchivePrompt(null);
    setArchiveReason("");
  };

  const handleArchive = async (row) => {
    setArchiving((s) => ({ ...s, [row.session_id]: true }));
    setArchiveError((s) => ({ ...s, [row.session_id]: null }));
    try {
      await axios.post(`${API}/foh/mot-session/${row.session_id}/archive`, {
        reason: archiveReason || "foh_dismissed_from_tile",
      });
      setRows((rs) => rs.filter((x) => x.session_id !== row.session_id));
      setArchivePrompt(null);
      setArchiveReason("");
    } catch (err) {
      setArchiveError((s) => ({
        ...s,
        [row.session_id]: err?.response?.data?.detail || "Could not archive",
      }));
    } finally {
      setArchiving((s) => ({ ...s, [row.session_id]: false }));
    }
  };

  useEffect(() => {
    fetchRows();
    // Re-poll every 60s so the tile stays warm without a hard reload.
    const handle = setInterval(fetchRows, 60_000);
    return () => clearInterval(handle);
  }, [fetchRows, refreshKey]);

  if (loading) return null;
  if (!rows.length) return null;

  return (
    <div className="mb-6" data-testid="mot-results-requiring-action-tile">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="w-4 h-4 text-teal-700" />
          <h2 className="text-lg font-semibold text-slate-800" data-testid="mot-results-title">
            MOT results requiring action
          </h2>
          <Badge className="bg-teal-100 text-teal-800 border-teal-200" data-testid="mot-results-count">
            {rows.length}
          </Badge>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchRows}
          data-testid="mot-results-refresh-btn"
        >
          <RefreshCw className="w-3 h-3 mr-1" />
          Refresh
        </Button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {rows.map((r) => {
          const pill = STATUS_PILL[r.workflow_status] || STATUS_PILL.PRICING_READY;
          const isManual = r.workflow_status === "MOT_RESULT_PENDING_MANUAL_REVIEW";
          return (
            <Card
              key={r.session_id}
              className={`border-2 ${
                isManual ? "border-amber-200 bg-amber-50" : "border-emerald-200 bg-emerald-50"
              } cursor-pointer hover:shadow-md transition-shadow`}
              onClick={() => {
                // Route policy for FOH clicks on this tile:
                //   • Imported & priced (yestimate exists) → FOH pricing
                //     screen so FOH can review numbers and send to customer.
                //   • Otherwise (manual review / failed auto-import) →
                //     /app/foh/job/{job_id}. FOHJobView embeds the
                //     MOTWorkflowControlCard at the top, so FOH lands on
                //     the manual-resolve / retry / archive controls
                //     directly — no bounce to tech surfaces.
                if (r.yestimate_id) {
                  navigate(`/app/foh/yestimate/${r.yestimate_id}/pricing`);
                } else if (r.job_id) {
                  navigate(`/app/foh/job/${r.job_id}`);
                } else if (r.session_id) {
                  navigate(`/app/foh/yestimate-intake/${r.session_id}`);
                }
              }}
              data-testid={`mot-results-row-${r.session_id}`}
            >
              <CardContent className="p-3 space-y-2">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    {isManual ? (
                      <AlertTriangle className="w-4 h-4 text-amber-700" />
                    ) : (
                      <CheckCircle2 className="w-4 h-4 text-emerald-700" />
                    )}
                    <span className="font-bold text-slate-900">{r.vrm}</span>
                    {r.customer_name && (
                      <span className="text-xs text-slate-600">
                        · {r.customer_name}
                      </span>
                    )}
                  </div>
                  <span
                    className={`px-2 py-0.5 rounded-full border text-[10.5px] font-semibold uppercase tracking-wide ${pill.cls}`}
                    data-testid={`mot-results-pill-${r.session_id}`}
                  >
                    {pill.label}
                  </span>
                </div>
                {r.test_result && (
                  <div className="flex items-center gap-2 text-xs text-slate-700">
                    <span
                      className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${
                        r.test_result === "PASSED"
                          ? "bg-emerald-200 text-emerald-900"
                          : "bg-red-200 text-red-900"
                      }`}
                    >
                      {r.test_result}
                    </span>
                    <span>
                      {r.defect_total} item{r.defect_total === 1 ? "" : "s"}
                    </span>
                    {r.defect_terminal > 0 && (
                      <span className="text-red-700 font-semibold">
                        · {r.defect_terminal} failure{r.defect_terminal === 1 ? "" : "s"}
                      </span>
                    )}
                    {r.defect_advisory > 0 && (
                      <span className="text-amber-700">
                        · {r.defect_advisory} advisor{r.defect_advisory === 1 ? "y" : "ies"}
                      </span>
                    )}
                    {r.repeat_advisory_count > 0 && (
                      <Badge
                        className="bg-purple-100 text-purple-800 border-purple-200 text-[10px]"
                        data-testid={`mot-results-repeat-${r.session_id}`}
                      >
                        {r.repeat_advisory_count} continuity warning{r.repeat_advisory_count === 1 ? "" : "s"}
                      </Badge>
                    )}
                  </div>
                )}
                {r.mot_slot_time && (
                  <div className="text-[10.5px] italic text-slate-500">
                    Slot {new Date(r.mot_slot_time).toLocaleString()}
                  </div>
                )}
                {/* Confirm Pass — appears only when the MOT passed and there
                    are zero actionable defects. One-click silent archive. */}
                {r.test_result === "PASSED" && (r.defect_total || 0) === 0 && (
                  <div onClick={(e) => e.stopPropagation()} className="pt-1">
                    <Button
                      size="sm"
                      onClick={() => handleConfirmPass(r)}
                      disabled={!!confirming[r.session_id]}
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-[12.5px] h-8"
                      data-testid={`mot-confirm-pass-btn-${r.session_id}`}
                    >
                      <Check className="w-3.5 h-3.5 mr-1.5" />
                      {confirming[r.session_id] ? "Confirming…" : "Confirm pass · archive"}
                    </Button>
                    {confirmError[r.session_id] && (
                      <p
                        className="text-[11px] text-red-700 mt-1"
                        data-testid={`mot-confirm-pass-error-${r.session_id}`}
                      >
                        {confirmError[r.session_id]}
                      </p>
                    )}
                  </div>
                )}

                {/* Archive (dismiss this row) — always available so FOH
                    can clear stuck rows that don't fit the confirm-pass
                    path (manual-review sessions that won't be actioned,
                    stale imports, cancelled customers, etc.). Two-step
                    with an optional reason for the audit trail. */}
                <div
                  onClick={(e) => e.stopPropagation()}
                  className="pt-1"
                  data-testid={`mot-archive-section-${r.session_id}`}
                >
                  {archivePrompt !== r.session_id ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => startArchive(r)}
                      className="w-full text-slate-600 hover:text-slate-900 hover:bg-slate-100 text-[11.5px] h-7"
                      data-testid={`mot-archive-btn-${r.session_id}`}
                    >
                      <Archive className="w-3 h-3 mr-1.5" />
                      Archive · remove from list
                    </Button>
                  ) : (
                    <div className="rounded-lg border border-slate-300 bg-white p-2 space-y-2">
                      <p className="text-[11px] font-semibold text-slate-700">
                        Reason (optional)
                      </p>
                      <input
                        type="text"
                        value={archiveReason}
                        onChange={(e) => setArchiveReason(e.target.value.slice(0, 280))}
                        placeholder="e.g. Customer cancelled / Not our job"
                        className="w-full text-[11.5px] px-2 py-1.5 border border-slate-300 rounded"
                        autoFocus
                        data-testid={`mot-archive-reason-${r.session_id}`}
                      />
                      <div className="flex items-center gap-1.5">
                        <Button
                          size="sm"
                          onClick={() => handleArchive(r)}
                          disabled={!!archiving[r.session_id]}
                          className="flex-1 bg-slate-700 hover:bg-slate-800 text-white text-[11.5px] h-7"
                          data-testid={`mot-archive-confirm-${r.session_id}`}
                        >
                          <Archive className="w-3 h-3 mr-1" />
                          {archiving[r.session_id] ? "Archiving…" : "Archive"}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={cancelArchive}
                          disabled={!!archiving[r.session_id]}
                          className="text-slate-500 text-[11.5px] h-7 px-2"
                          data-testid={`mot-archive-cancel-${r.session_id}`}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                      {archiveError[r.session_id] && (
                        <p
                          className="text-[11px] text-red-700"
                          data-testid={`mot-archive-error-${r.session_id}`}
                        >
                          {archiveError[r.session_id]}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default MOTResultsRequiringActionTile;
